import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Trash2, ArrowRight, User as UserIcon, Shield, Building2, Calendar, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAppContext } from '../../hooks/useAppContext';
import { Role, PermissionAssignment } from '../../types';
import { mockBranches } from '../../data/mockData';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { EmptyState } from '../../components/common/EmptyState';

const ModernPermissionFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (assignment: PermissionAssignment) => void;
}> = ({ isOpen, onClose, onSave }) => {
    const { users } = useAppStore();
    const employeeUsers = users.filter(u => String(u.role).toLowerCase() === 'employee');
    const initialUserId = employeeUsers?.[0]?.id ?? '';
    const [userId, setUserId] = useState<string>(initialUserId);
    const [role, setRole] = useState<Role>(Role.Employee);
    const [branch, setBranch] = useState<string>('');

    const roleOptions: { value: Role; label: string; description: string }[] = [
        { value: Role.Employee, label: 'موظف', description: 'صلاحيات محدودة، يمكنه تقديم التقارير فقط' },
        { value: Role.TeamLead, label: 'قائد فريق', description: 'إدارة فريق العمل ومتابعة المشاريع' },
        { value: Role.Admin, label: 'اداري', description: 'صلاحيات كاملة لإدارة النظام والمستخدمين' },
    ];

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!userId) return;
        const user = employeeUsers.find(u => u.id === userId);
        if (!user) return;

        const newAssignment: PermissionAssignment = {
            id: `P${Date.now()}`,
            userId: user.id,
            userName: user.name,
            role,
            assignedBranch: role === Role.Admin ? 'جميع الفروع' : undefined,
            assignmentDate: new Date().toISOString().split('T')[0],
        };
        onSave(newAssignment);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="glass-panel w-full max-w-lg p-6 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                        <h2 className="text-xl font-bold text-white">تعيين دور جديد</h2>
                        <button type="button" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                            <span className="sr-only">Close</span>
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-slate-300 mb-2 block">اختيار المستخدم</label>
                            <div className="relative">
                                <UserIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <select
                                    value={userId}
                                    onChange={e => setUserId(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                                >
                                    <option value="" disabled>اختر مستخدم...</option>
                                    {employeeUsers && employeeUsers.length > 0 ? (
                                        employeeUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)
                                    ) : (
                                        <option disabled>لا يوجد موظفين متاحين</option>
                                    )}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-300 mb-2 block">اختيار الدور</label>
                            <div className="space-y-2">
                                {roleOptions.map(r => (
                                    <label
                                        key={r.value}
                                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${role === r.value
                                                ? 'bg-primary/10 border-primary/50'
                                                : 'bg-slate-800/30 border-slate-700/50 hover:bg-slate-800/50'
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            name="role"
                                            value={r.value}
                                            checked={role === r.value}
                                            onChange={() => setRole(r.value)}
                                            className="mt-1"
                                        />
                                        <div>
                                            <span className={`block font-bold ${role === r.value ? 'text-primary' : 'text-white'}`}>{r.label}</span>
                                            <span className="text-xs text-slate-400">{r.description}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {role === Role.BranchManager && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                                <label className="text-sm font-medium text-slate-300 mb-2 block">اختيار الفرع</label>
                                <div className="relative">
                                    <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                    <select
                                        value={branch}
                                        onChange={e => setBranch(e.target.value)}
                                        className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                                    >
                                        <option value="">اختر الفرع...</option>
                                        {mockBranches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                        >
                            إلغاء
                        </button>
                        <button
                            type="submit"
                            disabled={!userId}
                            className="px-6 py-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            حفظ التعيين
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ModernManagePermissionsScreen: React.FC = () => {
    const { t } = useAppContext();
    const navigate = useNavigate();
    const { users, updateUser, openConfirmation } = useAppStore();
    const [isModalOpen, setModalOpen] = useState(false);

    // Normalize backend role strings to Role enum
    const normalizeRole = (r: string): Role => {
        const lower = String(r).toLowerCase();
        if (lower === 'employee') return Role.Employee;
        if (lower === 'admin') return Role.Admin;
        if (lower === 'teamlead') return Role.TeamLead;
        return r as Role;
    };

    // Derive assignments from current users (non-Employee roles)
    const assignments = useMemo(() => {
        return users
            .filter(u => String(u.role).toLowerCase() !== 'employee')
            .map((u) => ({
                id: `A-${u.id}`,
                userId: u.id,
                userName: u.name,
                role: normalizeRole(u.role as unknown as string),
                assignedBranch: String(u.role).toLowerCase() === 'admin' ? 'جميع الفروع' : undefined,
                assignmentDate: new Date().toISOString().split('T')[0],
            }));
    }, [users]);

    const handleSave = async (assignment: PermissionAssignment) => {
        const user = users.find(u => u.id === assignment.userId);
        if (!user) return;
        await updateUser({ ...user, role: assignment.role });
        toast.success('تم تعيين الدور بنجاح');
        setModalOpen(false);
    };

    const handleDelete = async (id: string) => {
        const target = assignments.find(a => a.id === id);
        if (!target) return;
        const user = users.find(u => u.id === target.userId);
        if (!user) return;

        openConfirmation('هل أنت متأكد من إزالة هذا الدور؟ سيتم تحويل المستخدم إلى موظف عادي.', async () => {
            await updateUser({ ...user, role: Role.Employee });
            toast.success('تم إزالة الدور بنجاح');
        });
    };

    // Map role to Arabic label for display
    const getRoleLabel = (role: Role) => {
        switch (role) {
            case Role.Employee: return 'موظف';
            case Role.TeamLead: return 'قائد فريق';
            case Role.Admin: return 'اداري';
            case Role.BranchManager: return 'مدير فرع';
            default: return String(role);
        }
    };

    const getRoleColor = (role: Role) => {
        switch (role) {
            case Role.Admin: return 'bg-red-500/10 text-red-400 border-red-500/20';
            case Role.TeamLead: return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
            case Role.BranchManager: return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
            default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">{t('managePermissions')}</h1>
                    <p className="text-slate-400">إدارة صلاحيات المستخدمين والأدوار</p>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                    >
                        <ArrowRight size={18} />
                        <span>رجوع</span>
                    </button>
                    <button
                        onClick={() => setModalOpen(true)}
                        className="px-6 py-2 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all flex items-center gap-2"
                    >
                        <PlusCircle size={18} />
                        <span>تعيين دور</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">إجمالي الإداريين</p>
                        <p className="text-3xl font-bold text-white">{assignments.filter(a => a.role === Role.Admin).length}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400">
                        <Shield size={24} />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">قادة الفرق</p>
                        <p className="text-3xl font-bold text-white">{assignments.filter(a => a.role === Role.TeamLead).length}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
                        <UserIcon size={24} />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">إجمالي الصلاحيات</p>
                        <p className="text-3xl font-bold text-white">{assignments.length}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                        <CheckCircle2 size={24} />
                    </div>
                </div>
            </div>

            {/* Permissions List */}
            <div className="glass-card rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-white/5">
                    <h2 className="text-xl font-bold text-white">الأدوار المعينة حالياً</h2>
                </div>

                {assignments.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left rtl:text-right text-slate-300">
                            <thead className="text-xs uppercase bg-slate-800/50 text-slate-400">
                                <tr>
                                    <th scope="col" className="px-6 py-4">المستخدم</th>
                                    <th scope="col" className="px-6 py-4">الدور</th>
                                    <th scope="col" className="px-6 py-4">الفرع المخصص</th>
                                    <th scope="col" className="px-6 py-4">تاريخ التعيين</th>
                                    <th scope="col" className="px-6 py-4">إجراء</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {assignments.map(a => (
                                    <tr key={a.id} className="hover:bg-white/5 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold border border-white/10">
                                                    {a.userName.charAt(0)}
                                                </div>
                                                <span className="font-medium text-white">{a.userName}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleColor(a.role)}`}>
                                                {getRoleLabel(a.role)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <Building2 size={14} />
                                                <span>{a.assignedBranch || '---'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <Calendar size={14} />
                                                <span>{new Date(a.assignmentDate).toLocaleDateString('ar-SA')}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleDelete(a.id)}
                                                className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                                                title="إزالة الصلاحية"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <EmptyState
                        icon={Shield}
                        title="لا توجد صلاحيات معينة"
                        message="لم يتم تعيين أي أدوار إضافية للموظفين حتى الآن."
                        action={<Button icon={<PlusCircle size={18} />} onClick={() => setModalOpen(true)}>تعيين دور</Button>}
                    />
                )}
            </div>

            <ModernPermissionFormModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
            />
        </div>
    )
};

export default ModernManagePermissionsScreen;
